'use client'

import { useEffect, useMemo } from 'react'

import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import {
  PERSONALIZATION_SESSION_KEY,
  usePersonalizationEligibility,
} from '@b-stock/fe-core/bstock-next/hooks'
import {
  AnalyticsEntityType,
  AnalyticsSource,
  FormattedMessage,
} from '@b-stock/fe-core/bstock-react'

import { BaseCarousel } from '@/components/common/carousel/BaseCarousel'
import { useListingSearch } from '@/components/listings/search/ListingSearchProvider'
import { defaultState } from '@/components/listings/search/ListingSearchProvider/defaultState'
import { LISTINGS_SORT_PERSONALIZED } from '@/components/listings/search/ListingSearchProvider/types'
import { listingSearchStateToSearchParams } from '@/components/listings/search/ListingSearchProvider/utils'
import { FeatureFlags } from '@/helpers/featureFlags'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'
import listingSearchQuery from '@/queries/listingSearchQuery'

import type { ApiCategory } from './apiCategories'
import { PERSONALIZED_CATEGORIES_CAROUSEL_NAME } from './constants'
import { generateViewMoreUrl } from './generateViewMoreUrl'
import { getRankingRationaleMessageId } from './rankingRationaleCopy'

interface PersonalizedCategoryCarouselProps {
  categories: ApiCategory[]
  excludeIds?: string[]
}

export default function PersonalizedCategoryCarousel({
  categories,
  excludeIds = [],
}: PersonalizedCategoryCarouselProps) {
  const { data: ssrData, isLoading } = useListingSearch()
  const { track } = useAnalytics()

  const arm = useFlag(FeatureFlags.PERSONALIZED_CAROUSEL_AB)
  const eligibility = usePersonalizationEligibility('/home-portal/api/headers')
  const isEligibleTreatment = arm === 'treatment' && eligibility === 'eligible'
  const isRankingRationaleLabelEnabled = useFlag(
    FeatureFlags.RANKING_RATIONALE_LABEL
  )
  const rankingRationaleArm = useFlag(FeatureFlags.RANKING_RATIONALE_LABEL_AB)

  const personalizationState = useMemo(
    () => ({
      ...defaultState,
      filters: {
        ...defaultState.filters,
        categories,
        listingType: ['SPOT'],
      },
      sortBy: LISTINGS_SORT_PERSONALIZED[0],
      sortOrder: LISTINGS_SORT_PERSONALIZED[1],
    }),
    // categories is stable after hydration (fixed from server-rendered account data)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [categories]
  )

  const personalizationQueryArg = useMemo(
    () => ({
      params: listingSearchStateToSearchParams(personalizationState),
      usePostEndpoint: true as const,
      fetchFilters: false,
    }),
    [personalizationState]
  )

  const { data: personalizedData } = listingSearchQuery.useWithOpts(
    [personalizationQueryArg],
    { enabled: isEligibleTreatment }
  )

  // Use personalized data when treatment+eligible and data has arrived; otherwise show SSR recommended data
  const data =
    isEligibleTreatment && personalizedData ? personalizedData : ssrData

  const excludeSet = new Set(excludeIds)
  const filteredData =
    data?.listings && excludeSet.size > 0
      ? {
          ...data,
          listings: data.listings.filter(
            (listing) => !excludeSet.has(listing.listingId)
          ),
        }
      : data

  const personalizationNotFound = data?.personalizationNotFound

  useEffect(() => {
    if (isEligibleTreatment && personalizationNotFound) {
      sessionStorage.setItem(PERSONALIZATION_SESSION_KEY, 'false')
    }
  }, [isEligibleTreatment, personalizationNotFound])

  const sortType: 'personalization' | 'recommended' =
    isEligibleTreatment && personalizedData && !personalizationNotFound
      ? 'personalization'
      : 'recommended'

  const viewMoreUrl = generateViewMoreUrl(categories, sortType)

  // `data` can be the SSR fallback (recommended) response, not just the
  // personalized one - only report model_version for the population that
  // was actually personalized, not whatever `data` happens to carry.
  const modelVersion =
    sortType === 'personalization' ? data?.modelVersion : undefined

  // Single source of truth for "the label could render for this sortType" —
  // both the rendered copy and the analytics tag derive from it, so they
  // can't drift out of sync with each other.
  const labelEligibleMessageId = isRankingRationaleLabelEnabled
    ? getRankingRationaleMessageId(sortType)
    : undefined

  const rationaleMessageId =
    rankingRationaleArm === 'treatment' ? labelEligibleMessageId : undefined

  // Only tag users for whom the label could actually render — otherwise the
  // treatment arm mixes in users who never see the label, diluting the
  // measured effect.
  const rationaleCarouselVariant = labelEligibleMessageId
    ? rankingRationaleArm
    : undefined

  const handleViewAllClick = () => {
    track('button_click', {
      button_name: 'carousel_view_all',
      carousel_name: PERSONALIZED_CATEGORIES_CAROUSEL_NAME,
      screen_name: 'homepage',
      source: AnalyticsSource.HOME_PORTAL,
      entityType: AnalyticsEntityType.BUYER,
      sort_type: sortType,
    })
  }

  return (
    <BaseCarousel
      data={filteredData}
      isLoading={isLoading}
      title={
        <FormattedMessage id="Common.Carousel.personalizedCategoryTitle" />
      }
      subtitle={
        rationaleMessageId && <FormattedMessage id={rationaleMessageId} />
      }
      viewMoreUrl={viewMoreUrl}
      viewMoreText={<FormattedMessage id="Common.Carousel.viewAll" />}
      onViewAllClick={handleViewAllClick}
      carouselName={PERSONALIZED_CATEGORIES_CAROUSEL_NAME}
      showMobileButton
      enableGTMTracking={false}
      sort_type={sortType}
      carousel_variant={rationaleCarouselVariant}
      model_version={modelVersion}
    />
  )
}
